/* =====================================================================
   gcx-ba: Before & after comparison (sections/gcx-ba.liquid, assets/gcx-ba.css)

   Per comparison (the [data-gcx-ba-stage] box):
   - the position p (0..100, % from the left) lives in the CSS variable
     --gcx-ba-p; CSS clips the after photo and places the line and handle.
   - mouse: press anywhere to jump there and drag; the handle keeps its grab
     offset. Touch and pen: a sideways drag moves the line (touch-action:
     pan-y pinch-zoom, so a vertical swipe scrolls the page and two fingers
     zoom as usual; a second finger ends our drag); a tap jumps there.
   - keyboard on the handle (role="slider"): arrows 2, Page Up / Page Down 10,
     Home / End. aria-valuenow / aria-valuetext follow every move; the value
     text comes from the section setting (tokens [before] [after]
     [before_pct] [after_pct]).
   - the Day 1 / 365 days labels fade when the line comes over them.
   - one gentle hint sweep the first time the section is mostly in view,
     never under prefers-reduced-motion, cancelled by any input.
   - each photo's 48px blurred copy (the loading placeholder) is hidden once
     the photo has loaded, so a photo with transparent parts never shows a
     blurred copy of itself behind it.
   Tabs (two or more comparisons): role="tablist", arrows / Home / End. The
   note under the photos follows the selected comparison ([data-gcx-ba-note],
   one per comparison, rendered by Liquid; the box hides when it is empty).
   Close-up toggle (a comparison with close-up photos): its box holds two
   pairs ([data-gcx-ba-view] layers); the toggle ([data-gcx-ba-views],
   role="tablist", arrows in the reading direction, Home / End) swaps them in
   place: the handle and its value stay, the incoming pair cross-fades in over
   the outgoing one (CSS, 350 ms; .is-prev holds the old pair until then), the
   hidden pair is aria-hidden. The choice applies to every comparison of the
   section. The close-up photos load with the other tabs' photos (eager()).
   Theme editor: re-inits on shopify:section:load, selects a block's tab on
   shopify:block:select.
   ===================================================================== */
(function () {
  'use strict';
  if (window.__gcxBaLoaded) { if (window.__gcxBaInit) window.__gcxBaInit(document); return; }
  window.__gcxBaLoaded = true;

  var RM = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(RM && RM.matches); }
  function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
  function ease(k) { return k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2; }
  var raf = window.requestAnimationFrame || function (f) { return setTimeout(function () { f(Date.now()); }, 16); };
  var caf = window.cancelAnimationFrame || clearTimeout;
  var now = function () { return (window.performance && performance.now) ? performance.now() : Date.now(); };

  function Cmp(stage) {
    var knob = stage.querySelector('[data-gcx-ba-knob]');
    var tagB = stage.querySelector('[data-gcx-ba-tag="before"]');
    var tagA = stage.querySelector('[data-gcx-ba-tag="after"]');
    var labB = knob.getAttribute('data-before') || '';
    var labA = knob.getAttribute('data-after') || '';
    var tpl = knob.getAttribute('data-valuetext') || '';
    var p = parseFloat(stage.style.getPropertyValue('--gcx-ba-p'));
    if (!isFinite(p)) p = 50;
    var W = 0, wB = 0, wA = 0, rectL = 0;
    var tween = 0, frame = 0, want = null, st = null;
    var self = { stage: stage, touched: false };

    function measure() {
      var r = stage.getBoundingClientRect();
      W = r.width; rectL = r.left;
      wB = tagB ? tagB.offsetWidth : 0;
      wA = tagA ? tagA.offsetWidth : 0;
    }
    function fade() {
      if (!W) return;
      var x = p / 100 * W, pad = W < 480 ? 10 : 14;
      if (tagB) tagB.classList.toggle('is-off', x < pad + wB + 10);
      if (tagA) tagA.classList.toggle('is-off', W - x < pad + wA + 10);
    }
    function aria() {
      var b = Math.round(p), a = 100 - b;
      knob.setAttribute('aria-valuenow', String(b));
      if (tpl) {
        knob.setAttribute('aria-valuetext', tpl
          .replace(/\[before_pct\]/g, String(b)).replace(/\[after_pct\]/g, String(a))
          .replace(/\[before\]/g, labB).replace(/\[after\]/g, labA));
      }
    }
    function paint(v, announce) {
      p = clamp(v, 0, 100);
      stage.style.setProperty('--gcx-ba-p', (Math.round(p * 100) / 100).toString());
      fade();
      if (announce !== false) aria();
    }
    // pointer moves are applied once per frame
    function queue(v) {
      want = v;
      if (!frame) frame = raf(function () { frame = 0; if (want !== null) { paint(want); want = null; } });
    }
    function stopTween() {
      if (tween) { caf(tween); tween = 0; }
      stage.classList.remove('is-hinting');
    }
    function tweenTo(to, dur, announce, done) {
      if (tween) { caf(tween); tween = 0; }
      if (reduced() || dur <= 0) { paint(to, announce); if (done) done(); return; }
      var from = p, t0 = now();
      function step() {
        var k = Math.min(1, (now() - t0) / dur);
        paint(from + (to - from) * ease(k), announce === false ? false : k === 1);
        if (k < 1) tween = raf(step); else { tween = 0; if (done) done(); }
      }
      tween = raf(step);
    }
    function pctAt(clientX) { return W ? (clientX - rectL) / W * 100 : p; }

    // ---- pointer input ----
    stage.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (st) {
        // a second finger (pinch-zoom): the browser takes the gesture, so let go of ours
        if (st.type !== 'mouse' && e.pointerType !== 'mouse' && e.pointerId !== st.id) end({ pointerId: st.id }, true);
        return;
      }
      stopTween();
      self.touched = true;
      measure();
      var onKnob = !!(e.target.closest && e.target.closest('[data-gcx-ba-knob]'));
      var at = pctAt(e.clientX);
      st = { id: e.pointerId, x: e.clientX, y: e.clientY, t: now(), type: e.pointerType, knob: onKnob, off: onKnob ? at - p : 0, drag: false };
      if (e.pointerType === 'mouse') {
        e.preventDefault(); // no text selection, no native image drag
        try { knob.focus({ preventScroll: true }); } catch (err) { knob.focus(); }
        begin(e);
        if (!onKnob) tweenTo(at, 160);
      } else if (onKnob) {
        // a finger on the handle moves it right away (sideways); a vertical swipe still scrolls
        st.drag = 'pending';
      }
    });
    function begin(e) {
      st.drag = true;
      stage.classList.add('is-dragging');
      try { stage.setPointerCapture(e.pointerId); } catch (err) { /* capture is optional */ }
    }
    stage.addEventListener('pointermove', function (e) {
      if (!st || e.pointerId !== st.id) return;
      if (st.drag !== true) {
        var dx = Math.abs(e.clientX - st.x), dy = Math.abs(e.clientY - st.y);
        if (dx < 6 || dx <= dy) return;
        begin(e);
      }
      stopTween();
      queue(pctAt(e.clientX) - st.off);
    });
    function end(e, cancelled) {
      if (!st || e.pointerId !== st.id) return;
      var s0 = st; st = null;
      stage.classList.remove('is-dragging');
      try { stage.releasePointerCapture(e.pointerId); } catch (err) { /* already released */ }
      if (cancelled) return;
      if (s0.drag !== true && s0.type !== 'mouse' && !s0.knob) {
        var moved = Math.abs(e.clientX - s0.x) + Math.abs(e.clientY - s0.y);
        if (moved < 10 && now() - s0.t < 700) tweenTo(pctAt(e.clientX), 240);
      } else if (s0.drag === true) {
        if (frame) { caf(frame); frame = 0; }
        if (want !== null) { paint(want); want = null; }
        aria();
      }
    }
    stage.addEventListener('pointerup', function (e) { end(e, false); });
    stage.addEventListener('pointercancel', function (e) { end(e, true); });
    // only the stage's own capture ending counts (moving a finger's implicit capture to the stage fires this on the child)
    stage.addEventListener('lostpointercapture', function (e) { if (e.target === stage && st && st.drag === true && e.pointerId === st.id) end(e, false); });
    stage.addEventListener('dragstart', function (e) { e.preventDefault(); });

    // ---- keyboard ----
    knob.addEventListener('keydown', function (e) {
      var k = e.key, b = Math.round(p), v = null;
      if (k === 'ArrowLeft' || k === 'ArrowDown' || k === 'Left' || k === 'Down') v = b - 2;
      else if (k === 'ArrowRight' || k === 'ArrowUp' || k === 'Right' || k === 'Up') v = b + 2;
      else if (k === 'PageDown') v = b - 10;
      else if (k === 'PageUp') v = b + 10;
      else if (k === 'Home') v = 0;
      else if (k === 'End') v = 100;
      if (v === null) return;
      e.preventDefault();
      stopTween();
      self.touched = true;
      measure();
      paint(v);
    });

    // ---- the one-time hint ----
    self.hint = function () {
      if (self.touched || reduced() || document.visibilityState === 'hidden') return;
      measure();
      if (!W) return;
      var home = p, d = W < 480 ? 12 : 10;
      stage.classList.add('is-hinting');
      tweenTo(clamp(home - d, 5, 95), 560, false, function () {
        tweenTo(clamp(home + d, 5, 95), 820, false, function () {
          tweenTo(home, 560, false, function () { stage.classList.remove('is-hinting'); paint(home); });
        });
      });
    };
    self.refresh = function () { measure(); fade(); };
    self.stop = stopTween;

    if (window.ResizeObserver) {
      new ResizeObserver(function () { self.refresh(); }).observe(stage);
    } else {
      window.addEventListener('resize', self.refresh);
    }
    measure(); fade();
    return self;
  }

  function reveal(img) {
    if (img.__gcxBa) return;
    img.__gcxBa = true;
    // the blurred copy right before the photo in the same layer
    var blur = img.previousElementSibling;
    if (blur && !(blur.classList && blur.classList.contains('gcx-ba__blur'))) blur = null;
    function on() { img.classList.add('is-in'); if (blur) blur.classList.add('is-gone'); }
    function err() { img.classList.add('is-in'); } // keep the blurred copy if the photo fails
    if (img.complete && img.naturalWidth) on();
    else { img.addEventListener('load', on); img.addEventListener('error', err); }
  }

  function initRoot(root) {
    if (root.__gcxBa) return;
    root.__gcxBa = true;
    root.classList.add('is-js');
    var imgs = root.querySelectorAll('.gcx-ba__img');
    for (var i = 0; i < imgs.length; i++) reveal(imgs[i]);

    var panels = root.querySelectorAll('[data-gcx-ba-panel]');
    var cmps = [];
    for (var j = 0; j < panels.length; j++) {
      var stg = panels[j].querySelector('[data-gcx-ba-stage]');
      cmps.push(stg && stg.querySelector('[data-gcx-ba-knob]') ? Cmp(stg) : null);
    }
    var tabs = root.querySelectorAll('[data-gcx-ba-tab]');
    var foot = root.querySelector('[data-gcx-ba-foot]');
    var notes = root.querySelectorAll('[data-gcx-ba-note]');
    var cur = 0;
    for (var q = 0; q < panels.length; q++) if (!panels[q].hidden) { cur = q; break; }

    var eagered = false;
    function eager() {
      if (eagered) return;
      eagered = true;
      var li = root.querySelectorAll('img[loading="lazy"]');
      for (var n = 0; n < li.length; n++) li[n].loading = 'eager';
    }

    // ---- the close-up toggle: one choice for the whole section ----
    var vlists = root.querySelectorAll('[data-gcx-ba-views]');
    function showView(panel, v, animate) {
      var layers = panel.querySelectorAll('[data-gcx-ba-view]');
      if (layers.length < 2) return;
      var want = Math.min(v, layers.length - 1), next = layers[want], prev = null;
      for (var j = 0; j < layers.length; j++) if (layers[j].classList.contains('is-on')) prev = layers[j];
      var vt = panel.querySelectorAll('[data-gcx-ba-vtab]');
      for (var t = 0; t < vt.length; t++) {
        vt[t].setAttribute('aria-selected', t === want ? 'true' : 'false');
        vt[t].tabIndex = t === want ? 0 : -1;
      }
      if (prev === next) return;
      if (prev) {
        prev.classList.remove('is-on');
        prev.setAttribute('aria-hidden', 'true');
        if (animate && !reduced()) {
          prev.classList.add('is-prev');
          clearTimeout(prev.__gcxBaT);
          (function (el) { el.__gcxBaT = setTimeout(function () { el.classList.remove('is-prev'); }, 400); })(prev);
        }
      }
      clearTimeout(next.__gcxBaT);
      next.classList.remove('is-prev');
      next.classList.add('is-on');
      next.removeAttribute('aria-hidden');
    }
    function setView(v, fromList) {
      eager();
      for (var k = 0; k < panels.length; k++) showView(panels[k], v, !panels[k].hidden);
      if (fromList) {
        var vt = fromList.querySelectorAll('[data-gcx-ba-vtab]');
        if (vt[v]) vt[v].focus();
      }
    }
    for (var vl = 0; vl < vlists.length; vl++) {
      (function (list) {
        var vt = list.querySelectorAll('[data-gcx-ba-vtab]');
        for (var t = 0; t < vt.length; t++) {
          (function (idx) { vt[idx].addEventListener('click', function () { setView(idx, null); }); })(t);
        }
        list.addEventListener('keydown', function (e) {
          var k = e.key, rtl = getComputedStyle(list).direction === 'rtl', at = 0, to = null;
          for (var q = 0; q < vt.length; q++) if (vt[q].getAttribute('aria-selected') === 'true') at = q;
          if (k === 'ArrowRight' || k === 'Right') to = at + (rtl ? -1 : 1);
          else if (k === 'ArrowLeft' || k === 'Left') to = at + (rtl ? 1 : -1);
          else if (k === 'Home') to = 0;
          else if (k === 'End') to = vt.length - 1;
          if (to === null) return;
          e.preventDefault();
          setView((to + vt.length) % vt.length, list);
        });
        list.addEventListener('pointerenter', eager);
        list.addEventListener('focusin', eager);
        list.addEventListener('touchstart', eager, { passive: true });
      })(vlists[vl]);
    }

    function select(i, focus) {
      if (i < 0 || i >= panels.length) return;
      for (var t = 0; t < tabs.length; t++) {
        var on = t === i;
        tabs[t].setAttribute('aria-selected', on ? 'true' : 'false');
        tabs[t].tabIndex = on ? 0 : -1;
      }
      for (var k = 0; k < panels.length; k++) panels[k].hidden = k !== i;
      // the note that belongs to this comparison (its own labels), or none
      var has = false;
      for (var m = 0; m < notes.length; m++) {
        var mine = notes[m].getAttribute('data-gcx-ba-note') === String(i + 1);
        notes[m].hidden = !mine;
        if (mine && notes[m].textContent.replace(/\s+/g, '')) has = true;
      }
      if (foot) foot.hidden = !has;
      if (cmps[cur] && cur !== i) cmps[cur].stop();
      cur = i;
      eager();
      if (focus && tabs[i]) tabs[i].focus();
      if (cmps[i]) cmps[i].refresh();
    }
    root.__gcxBaSelect = function (el) {
      for (var k = 0; k < panels.length; k++) if (panels[k] === el || panels[k].contains(el)) { select(k, false); return; }
    };

    if (tabs.length) {
      var list = root.querySelector('[data-gcx-ba-tabs]');
      for (var t = 0; t < tabs.length; t++) {
        (function (idx) {
          tabs[idx].addEventListener('click', function () { select(idx, false); });
        })(t);
      }
      list.addEventListener('keydown', function (e) {
        var k = e.key, rtl = getComputedStyle(list).direction === 'rtl', to = null;
        if (k === 'ArrowRight' || k === 'Right') to = cur + (rtl ? -1 : 1);
        else if (k === 'ArrowLeft' || k === 'Left') to = cur + (rtl ? 1 : -1);
        else if (k === 'Home') to = 0;
        else if (k === 'End') to = tabs.length - 1;
        if (to === null) return;
        e.preventDefault();
        select((to + tabs.length) % tabs.length, true);
      });
      list.addEventListener('pointerenter', eager);
      list.addEventListener('focusin', eager);
      list.addEventListener('touchstart', eager, { passive: true });
    }

    // hint once the section is mostly in view; the other tabs' photos load after a moment
    var wantHint = root.hasAttribute('data-gcx-ba-hint');
    var media = root.querySelector('.gcx-ba__media') || root;
    if ('IntersectionObserver' in window) {
      var shown = false;
      var io = new IntersectionObserver(function (entries) {
        for (var n = 0; n < entries.length; n++) {
          var en = entries[n];
          if (!en.isIntersecting) continue;
          if (!shown) { shown = true; setTimeout(eager, 1500); }
          var c = cmps[cur];
          if (!wantHint || !c) { io.disconnect(); return; }
          var r = c.stage.getBoundingClientRect(), vh = window.innerHeight || document.documentElement.clientHeight;
          var vis = Math.min(r.bottom, vh) - Math.max(r.top, 0);
          if (vis >= Math.min(r.height, vh) * 0.6) {
            io.disconnect();
            setTimeout(function () { if (cmps[cur]) cmps[cur].hint(); }, 350);
          }
        }
      }, { threshold: [0, 0.25, 0.5, 0.6, 0.75, 1] });
      io.observe(media);
    } else {
      setTimeout(eager, 3000);
    }
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll ? (scope || document).querySelectorAll('[data-gcx-ba]') : [];
    for (var i = 0; i < roots.length; i++) initRoot(roots[i]);
    if (scope && scope.matches && scope.matches('[data-gcx-ba]')) initRoot(scope);
  }
  window.__gcxBaInit = initAll;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  else initAll(document);
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target && e.target.closest ? e.target.closest('[data-gcx-ba]') : null;
    if (root && root.__gcxBaSelect) root.__gcxBaSelect(e.target);
  });
})();
